import { Instagram, Facebook, Phone, Mail, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Heart, Star, Rainbow, Squiggle, ChildWithBalloons } from "./Illustrations";

export default function Footer() {
  const navigate = useNavigate();
  const handleNav = (id) => {
    if (id === "hero") {
      navigate("/");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (id === "choroi") {
      navigate("/oi-choroi-mas");
      return;
    }
    navigate(`/#${id}`);
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-gradient-to-br from-brand-pinkSoft via-brand-yellowSoft to-brand-blueSoft text-brand-text pt-20 pb-10 relative overflow-hidden border-t-4 border-dashed border-brand-pink"
    >
      <Rainbow className="absolute -top-10 left-1/2 -translate-x-1/2 opacity-30 hidden md:block" size={400} />
      <Heart className="absolute top-20 left-12 animate-pulse-soft hidden md:block" size={32} color="#F4A899" />
      <Star className="absolute top-32 right-16 animate-pulse-soft" size={28} color="#FBE4A8" />
      <ChildWithBalloons className="absolute -bottom-2 right-10 hidden lg:block animate-float" size={120} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-12 gap-10 pb-14 border-b-2 border-dashed border-brand-text/15">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center overflow-hidden p-1 border-2 border-brand-yellow">
                <img
                  src="/images/logo.png"
                  alt="Ορίζοντες Ανάπτυξης"
                  className="w-full h-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div>
                <div className="font-heading font-bold text-xl">Ορίζοντες Ανάπτυξης</div>
                <div className="font-hand text-base text-brand-primary leading-none">
                  Κέντρο Ειδικών Θεραπειών
                </div>
              </div>
            </div>
            <p className="font-body text-brand-textSoft leading-relaxed max-w-md">
              Εξειδικευμένη φροντίδα και υποστήριξη για παιδιά και οικογένειες
              στο Περιστέρι και τα Δυτικά Προάστια της Αθήνας, σε ένα ζεστό
              περιβάλλον με σεβασμό και αγάπη.
            </p>
            <Squiggle className="my-5" width={120} color="#F4A899" />
            <div className="flex flex-wrap gap-3">
              <a
                href="https://instagram.com/orizontes.anaptixis"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                aria-label="Instagram"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border-2 border-brand-pink text-brand-primary font-bold text-sm hover:bg-brand-pinkSoft transition-colors shadow-[0_3px_0_#5B4F46]/15"
              >
                <Instagram size={18} />
                @orizontes.anaptixis
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61594498642937"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                aria-label="Facebook"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border-2 border-brand-blue text-brand-primaryHover font-bold text-sm hover:bg-brand-blueSoft transition-colors shadow-[0_3px_0_#5B4F46]/15"
              >
                <Facebook size={18} />
                Orizontes Anaptixis
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h4 className="font-heading font-bold text-base mb-5 text-brand-primaryHover">Πλοήγηση</h4>
            <ul className="space-y-3 font-body text-sm text-brand-textSoft">
              {[
                { l: "Αρχική", id: "hero" },
                { l: "Υπηρεσίες", id: "ypiresies" },
                { l: "Οι Χώροι μας", id: "choroi" },
                { l: "Φιλοσοφία", id: "filosofia" },
                { l: "Η Ομάδα μας", id: "omada" },
                { l: "Επικοινωνία", id: "epikoinonia" },
              ].map((item) => (
                <li key={item.id} className="flex items-center gap-2">
                  <Heart size={14} color="#F4A899" />
                  <button
                    data-testid={`footer-nav-${item.id}`}
                    onClick={() => handleNav(item.id)}
                    className="hover:text-brand-primary font-bold transition-colors"
                  >
                    {item.l}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="font-heading font-bold text-base mb-5 text-brand-primaryHover">Επικοινωνία</h4>
            <ul className="space-y-4 font-body text-sm text-brand-textSoft">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 text-brand-primary flex-shrink-0" />
                <span>Υψούντος 7, Περιστέρι<br /><span className="text-brand-textSoft/70 text-xs">Μετρό Αγ. Αντωνίου</span></span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-brand-primary flex-shrink-0" />
                <a href="tel:+302105719375" className="hover:text-brand-primary font-bold transition-colors">
                  210 571 9375
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-brand-primary flex-shrink-0" />
                <a href="mailto:orizontesanaptixis@hotmail.com" className="hover:text-brand-primary font-bold transition-colors break-all">
                  orizontesanaptixis@hotmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-sm text-brand-textSoft">
            © {new Date().getFullYear()} Ορίζοντες Ανάπτυξης. Με επιφύλαξη παντός δικαιώματος.
          </p>
          <p className="font-hand text-base text-brand-primary flex items-center gap-2">
            Φτιαγμένο με <Heart size={18} color="#F4A899" /> για κάθε παιδί
          </p>
        </div>
      </div>
    </footer>
  );
}
